import { z } from "zod";
import { idSchema, sourceDateSchema } from "@amber/contracts";

export const newsArticleSchema = z.object({
  schema: z.literal("amber.news-article/1"),
  supplier: z.string(),
  uri: z.string(),
  url: z.string(),
  title: z.string(),
  body: z.string(),
  authors: z.array(z.string()),
  source: z.string(),
  published_at: z.string().nullable(),
  lang: z.string().nullable(),
  links: z.array(z.string()),
  text_basis: z.literal("supplier_text"),
  page_capture: z.object({ state: z.literal("failed"), route: z.string().nullable(), detail: z.string() }),
  observed_at: z.string(),
});
export type NewsArticleRecord = z.infer<typeof newsArticleSchema>;

/** Profiles hold ISO 639-1; the news supplier speaks ISO 639-3. A language missing here searches every language. */
export const ISO_639_3: ReadonlyMap<string, string> = new Map([
  ["ar", "ara"],
  ["de", "deu"],
  ["en", "eng"],
  ["es", "spa"],
  ["fr", "fra"],
  ["it", "ita"],
  ["ja", "jpn"],
  ["nl", "nld"],
  ["pl", "pol"],
  ["pt", "por"],
  ["ru", "rus"],
  ["sv", "swe"],
  ["tr", "tur"],
  ["uk", "ukr"],
  ["zh", "zho"],
]);

/** DI-60: the ISO 639-1 code for a supplier's ISO 639-3 code, bibliographic variants included. */
export const ISO_639_1: ReadonlyMap<string, string> = new Map([
  ...[...ISO_639_3].map(([two, three]) => [three, two] as const),
  ["chi", "zh"],
  ["dut", "nl"],
  ["fre", "fr"],
  ["ger", "de"],
]);

export const NEWS_TEXT_BASIS_LABEL = "Supplier text · the publisher's page could not be captured";

export const supplierCopySchema = z.object({
  supplier: z.string(),
  uri: z.string(),
  url: z.string(),
  title: z.string(),
  body: z.string(),
  authors: z.array(z.string()),
  source: z.string(),
  published_at: z.string().nullable(),
  lang: z.string().nullable(),
  links: z.array(z.string()),
  raw_storage_key: z.string(),
  raw_sha256: z.string(),
  observed_at: z.string(),
  /**
   * DI-9: syndicated copies of the same article that the supplier listed; they collapse into this item. A copy the
   * supplier skipped as a duplicate is known by its URI alone.
   */
  syndicated: z
    .array(z.object({ uri: z.string(), url: z.string().optional(), source: z.string().optional() }))
    .default([]),
});
export type SupplierCopy = z.infer<typeof supplierCopySchema>;

/** DI-9, DI-96: what a supplier-backed source can search, recorded on the source at each scan. */
export const supplierCoverageSchema = z.object({
  supplier: z.string(),
  archive_from: z.string().nullable(),
  archive_note: z.string(),
  page_size: z.number().int().positive(),
  max_pages: z.number().int().positive(),
  recorded_at: z.string(),
});
export type SupplierCoverage = z.infer<typeof supplierCoverageSchema>;

export const NAMED_ORIGINAL_OUTCOMES = ["linked", "lead", "unresolved"] as const;

/** DI-60: an original communication a news report names, and what following it found. */
export const namedOriginalSchema = z.strictObject({
  kind: z.enum(["interview", "post", "speech"]),
  phrase: z.string(),
  outlet: z.string().nullable(),
  platform: z.string().nullable(),
  date: sourceDateSchema.nullable(),
  outcome: z.enum(NAMED_ORIGINAL_OUTCOMES),
  source_id: idSchema("src").nullable(),
  item_id: idSchema("itm").nullable(),
  detail: z.string(),
});
export type NamedOriginalRecord = z.infer<typeof namedOriginalSchema>;

export const namedOriginalViewSchema = z.strictObject({
  text: z.string(),
  outcome: z.enum(NAMED_ORIGINAL_OUTCOMES),
  item_id: idSchema("itm").nullable(),
});
